import type { ScheduledDashboardData } from '../../../lib/api/scheduled-dashboard';

export const labelClass =
  'text-[10px] text-muted-foreground font-medium uppercase tracking-wide leading-tight';
export const footClass = 'text-[10px] text-muted-foreground mt-1';

export interface KPICellProps {
  label: string;
  value: React.ReactNode;
  foot?: string;
  /**
   * Optional click handler. When provided, the cell renders with a hover
   * background, cursor pointer, and a small arrow glyph indicating the cell
   * is interactive (drill-down). Keyboard accessible.
   */
  onClick?: () => void;
}

export function KPICell({ label, value, foot, onClick }: KPICellProps) {
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="group relative flex flex-col items-center text-center gap-1 cursor-pointer rounded-md p-1.5 -m-1.5 transition-colors hover:bg-slate-100/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
      >
        <span
          aria-hidden
          className="absolute right-1 top-1 text-[10px] font-medium text-slate-300 group-hover:text-slate-500 transition-colors"
        >
          ↗
        </span>
        <p className={labelClass}>{label}</p>
        <div className="flex items-center justify-center min-h-[44px] w-full">{value}</div>
        {foot && <p className={footClass}>{foot}</p>}
      </button>
    );
  }
  return (
    <div className="flex flex-col items-center text-center gap-1">
      <p className={labelClass}>{label}</p>
      <div className="flex items-center justify-center min-h-[44px] w-full">{value}</div>
      {foot && <p className={footClass}>{foot}</p>}
    </div>
  );
}

export function Placeholder({ note = '—' }: { note?: string }) {
  return <span className="text-base font-semibold text-muted-foreground/60">{note}</span>;
}

export interface PeriodContentProps {
  data: ScheduledDashboardData;
  formatCurrency: (n: number) => string;
}

export function SectionFrame({
  title, accent, subtitle, children,
}: {
  title: string;
  accent: 'red' | 'green' | 'blue';
  subtitle?: string;
  children: React.ReactNode;
}) {
  const ring = accent === 'red'
    ? 'border-red-200 bg-red-50/30'
    : accent === 'green'
      ? 'border-green-200 bg-green-50/30'
      : 'border-blue-200 bg-blue-50/30';
  const titleColor = accent === 'red'
    ? 'text-red-600'
    : accent === 'green'
      ? 'text-green-600'
      : 'text-blue-600';
  return (
    <div className={`border ${ring} rounded-lg p-3 flex flex-col`}>
      <div className="flex items-center justify-between mb-2">
        <h4 className={`font-semibold ${titleColor} text-xs uppercase tracking-wide`}>{title}</h4>
        {subtitle && <span className="text-[10px] text-muted-foreground">{subtitle}</span>}
      </div>
      {children}
    </div>
  );
}

// Stub trend data — same shape backend will return so swapping is mechanical.
export function makeStubTrend(label: string, points: number, base = 0): Array<{ x: string; y: number }> {
  void label;
  return Array.from({ length: points }, (_, i) => ({ x: `W${i + 1}`, y: base }));
}

/** Tier a 0-100 percentage to a colour relative to a target. */
export function performanceColor(pct: number, target = 95): string {
  if (pct >= target) return '#0f766e';        // teal — at/above target
  if (pct >= target - 15) return '#d97706';   // amber — within 15pp of target
  return '#dc2626';                            // red — well below target
}

/** Drop a sentinel-zero series into ``null`` so charts render breaks where
 *  there genuinely was no data, rather than a misleading 0% point. */
export function nullIfNoData<T extends { total_with_status: number; otif_pct: number; otd_pct: number }>(
  row: T,
): { otif: number | null; otd: number | null } {
  if (!row.total_with_status) return { otif: null, otd: null };
  return { otif: row.otif_pct, otd: row.otd_pct };
}
